import { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import ProductCard from '@/components/ProductCard';

const ArrowButton = ({ onClick, disabled, label, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    className={cn(
      'w-10 h-10 md:w-11 md:h-11 rounded-full flex items-center justify-center border transition-all duration-200',
      'border-burgundy-500/30 bg-white text-burgundy-500 shadow-sm',
      'hover:bg-burgundy-500 hover:text-white hover:border-burgundy-500 hover:shadow-md',
      'disabled:opacity-35 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-burgundy-500 disabled:hover:border-burgundy-500/30 disabled:hover:shadow-sm'
    )}
  >
    {children}
  </button>
);

// سلايدر منتجات أفقي (RTL): سحب باللمس/الماوس + أسهم + نقاط تنقّل
const ProductSlider = ({ products }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    direction: 'rtl',
    align: 'start',
    containScroll: 'trimSnaps',
    slidesToScroll: 'auto',
    dragFree: false,
  });
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);
  const [snaps, setSnaps] = useState([]);
  const [selected, setSelected] = useState(0);

  const sync = useCallback((api) => {
    setCanPrev(api.canScrollPrev());
    setCanNext(api.canScrollNext());
    setSelected(api.selectedScrollSnap());
    setSnaps(api.scrollSnapList());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;
    sync(emblaApi);
    emblaApi.on('select', sync).on('reInit', sync);
    return () => {
      emblaApi.off('select', sync).off('reInit', sync);
    };
  }, [emblaApi, sync]);

  const hasMultiplePages = snaps.length > 1;

  return (
    <div className="relative">
      <div ref={emblaRef} className="overflow-hidden -my-4 py-4" dir="rtl">
        <div className="flex -mr-4 md:-mr-6 touch-pan-y">
          {products.map((product) => (
            <div
              key={product.id}
              className="min-w-0 shrink-0 grow-0 basis-1/2 md:basis-1/3 lg:basis-1/4 xl:basis-1/5 pr-4 md:pr-6"
            >
              <ProductCard product={product} />
            </div>
          ))}
        </div>
      </div>

      {hasMultiplePages && (
        <div className="mt-6 md:mt-8 flex items-center justify-between gap-4">
          <div className="flex items-center gap-1.5" role="tablist" aria-label="صفحات السلايدر">
            {snaps.map((_, i) => (
              <button
                key={i}
                type="button"
                role="tab"
                aria-selected={i === selected}
                aria-label={`الصفحة ${i + 1}`}
                onClick={() => emblaApi?.scrollTo(i)}
                className={cn(
                  'h-2 rounded-full transition-all duration-300',
                  i === selected ? 'w-7 bg-burgundy-500' : 'w-2 bg-gray-300 hover:bg-gray-400'
                )}
              />
            ))}
          </div>

          <div className="flex items-center gap-2">
            <ArrowButton onClick={() => emblaApi?.scrollPrev()} disabled={!canPrev} label="السابق">
              <ChevronRight className="w-5 h-5" />
            </ArrowButton>
            <ArrowButton onClick={() => emblaApi?.scrollNext()} disabled={!canNext} label="التالي">
              <ChevronLeft className="w-5 h-5" />
            </ArrowButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductSlider;
